/**
 * Customer Service
 * API methods for customer profile and address management
 */

import api from '../utils/api';

export const customerService = {
  /**
   * Get customer profile
   * @returns {Promise} API response with customer data
   */
  getProfile: () => {
    return api.get('/customer');
  },

  /**
   * Update customer profile
   * @param {Object} data - Profile data to update
   * @param {string} data.fullName - Customer full name
   * @param {string} data.email - Customer email (optional)
   * @param {Object} data.currentLocation - Current location coordinates
   * @returns {Promise} API response with updated customer data
   */
  updateProfile: (data) => {
    return api.put('/customer', data);
  },

  /**
   * Add new address
   * @param {Object} addressData - Address data
   * @param {string} addressData.addressType - Type (Home, Office, Shipping, Billing)
   * @param {string} addressData.houseNumber - House/building number
   * @param {string} addressData.street - Street name
   * @param {string} addressData.area - Area/locality
   * @param {string} addressData.landmark - Nearby landmark
   * @param {string} addressData.city - City (required)
   * @param {string} addressData.state - State (required)
   * @param {string} addressData.country - Country (required)
   * @param {string} addressData.pincode - Postal code (required)
   * @param {number} addressData.latitude - Latitude (required)
   * @param {number} addressData.longitude - Longitude (required)
   * @param {boolean} addressData.isDefault - Set as default address
   * @returns {Promise} API response with added address
   */
  addAddress: (addressData) => {
    return api.post('/customer/address', addressData);
  },

  /**
   * Update existing address
   * @param {string} addressId - Address ID to update
   * @param {Object} addressData - Address data to update
   * @returns {Promise} API response with updated address
   */
  updateAddress: (addressId, addressData) => {
    return api.put(`/customer/address/${addressId}`, addressData);
  },

  /**
   * Delete address
   * @param {string} addressId - Address ID to delete
   * @returns {Promise} API response
   */
  deleteAddress: (addressId) => {
    return api.delete(`/customer/address/${addressId}`);
  },

  /**
   * Deactivate customer account (soft delete)
   * Sets isActive to false instead of permanent deletion
   * @returns {Promise} API response
   */
  deactivateAccount: () => {
    return api.delete('/customer');
  },
};

export default customerService;
